import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
} from "@reduxjs/toolkit";

import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  collection,
  onSnapshot,
  query,
  where,
  getDocs,
} from "firebase/firestore";

import { db } from "../firebase";
import { AppDispatch, RootState } from "./store";

/* ─────────────────── TYPES ─────────────────── */

export interface GroceryOrder {
  id:             string;
  guestId:        string;
  hostId:         string;
  items:          GroceryOrderItem[];
  subtotal:       number;
  totalQty:       number;
  totalPaid:      number;
  guestsFee:      number;
  hostsFee:       number;
  hostProfit:     number;
  platformProfit: number;
  paymentId:      string;
  deliveryAddress?: {
    street?:      string;
    city?:        string;
    country?:     string;
    latitude?:    number;
    longitude?:   number;
  };
  isConfirmed:    boolean;
  isCancelled:    boolean;
  isRefunded:     boolean;
  isPaid:         boolean;
  isDelivered:    boolean;
  createdAt?:     any;
  updatedAt?:     any;
  [key: string]:  any;
}

export interface GroceryOrderItem {
  listingId:      string;
  name?:          string;
  hostId:         string;
  brandId?:       string;
  qty:            number;
  price?:         number;
  rate?:          number;
  unit?:          string;
  image?:         string;
  photos?:        string[];
  isOrganic?:     boolean;
  isFresh?:       boolean;
  isFreeRange?:   boolean;
  deliveryStatus?: {
    sellerDelivered?: boolean;
    buyerReceived?:   boolean;
    orderDelivered?:  boolean;
  };
  [key: string]:  any;
}

interface GroceriesOrderState {
  orders:              GroceryOrder[];
  currentOrder:        GroceryOrder | null;
  error:               string | null;
  unsubscribe?:        () => void;
  /* stats */
  hostDeliveredCount:  number;
  hostDeliveredProfit: number;
}

/* ─────────────────── HELPERS ─────────────────── */

const safeMillis = (ts: any): number | null =>
  ts?.toMillis?.() ?? (typeof ts === "number" ? ts : null);

const parseOrder = (id: string, data: any): GroceryOrder => ({
  ...data,
  id,
  createdAt: safeMillis(data.createdAt),
  updatedAt: safeMillis(data.updatedAt),
});

/* ─────────────────── INITIAL STATE ─────────────────── */

const initialState: GroceriesOrderState = {
  orders:              [],
  currentOrder:        null,
  error:               null,
  unsubscribe:         undefined,
  hostDeliveredCount:  0,
  hostDeliveredProfit: 0,
};

/* ─────────────────── UPSERT ORDER ─────────────────── */

export const upsertGroceryOrder = createAsyncThunk<
  GroceryOrder,
  Partial<GroceryOrder> & {
    paymentId: string;
    hostId?: string;
    listingId?: string;
    deliveryStatus?: {
      sellerDelivered?: boolean;
      buyerReceived?:   boolean;
      orderDelivered?:  boolean;
    };
  },
  { rejectValue: string }
>(
  "groceryOrders/upsert",
  async (orderData, { rejectWithValue }) => {
    try {
      if (!orderData.paymentId) throw new Error("Missing paymentId");

      const docRef = doc(db, "GroceryOrders", orderData.paymentId);
      const snap   = await getDoc(docRef);
      if (!snap.exists()) throw new Error("Order does not exist");

      const existing = snap.data() as GroceryOrder;
      const payload: any = { updatedAt: serverTimestamp() };

      /* ── update item delivery statuses ── */
      if (orderData.deliveryStatus) {
        const updatedItems = existing.items.map((item: GroceryOrderItem) => {

          /* host marks shipped / packed */
          if (
            orderData.hostId &&
            item.hostId === orderData.hostId &&
            orderData.deliveryStatus?.sellerDelivered === true
          ) {
            return {
              ...item,
              deliveryStatus: { ...item.deliveryStatus, sellerDelivered: true },
            };
          }

          /* buyer marks received */
          if (
            orderData.listingId &&
            item.listingId === orderData.listingId &&
            orderData.deliveryStatus?.buyerReceived === true
          ) {
            return {
              ...item,
              deliveryStatus: {
                ...item.deliveryStatus,
                buyerReceived: true,
                orderDelivered: true,
              },
            };
          }

          return item;
        });

        payload.items = updatedItems;

        const allDone = updatedItems.every(
          (i) => i.deliveryStatus?.sellerDelivered && i.deliveryStatus?.buyerReceived
        );
        if (allDone) payload.isDelivered = true;
      }

      await updateDoc(docRef, payload);
      return parseOrder(orderData.paymentId, { ...existing, ...payload });
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to upsert grocery order");
    }
  }
);

/* ─────────────────── LISTENERS ─────────────────── */

/* Single order */
export const listenToGroceryOrder =
  (orderId: string) =>
  (dispatch: AppDispatch) => {
    const unsub = onSnapshot(
      doc(db, "GroceryOrders", orderId),
      (snap) => {
        dispatch(
          groceriesOrderSlice.actions.setCurrentOrder(
            snap.exists() ? parseOrder(snap.id, snap.data()!) : null
          )
        );
      },
      (err) => dispatch(groceriesOrderSlice.actions.setError(err.message))
    );
    dispatch(groceriesOrderSlice.actions.setUnsubscribe(unsub));
    return unsub;
  };

/* Guest orders */
export const listenToGuestGroceryOrders =
  (guestId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!guestId) return;
    const { unsubscribe } = getState().groceryOrders;
    if (unsubscribe) return;

    const unsub = onSnapshot(
      query(collection(db, "GroceryOrders"), where("guestId", "==", guestId)),
      (snap) => {
        dispatch(
          groceriesOrderSlice.actions.setOrders(
            snap.docs.map((d) => parseOrder(d.id, d.data()))
          )
        );
      },
      (err) => dispatch(groceriesOrderSlice.actions.setError(err.message))
    );

    dispatch(groceriesOrderSlice.actions.setUnsubscribe(unsub));
    return unsub;
  };

/* Host orders — only confirmed, only items belonging to this host */
export const listenToHostGroceryOrders =
  (hostId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!hostId) return;
    const { unsubscribe } = getState().groceryOrders;
    if (unsubscribe) return;

    const unsub = onSnapshot(
      query(
        collection(db, "GroceryOrders"),
        where("isConfirmed", "==", true)
      ),
      (snap) => {
        const orders: GroceryOrder[] = snap.docs
          .map((d) => {
            const data = d.data();
            const hostItems = (data.items ?? []).filter(
              (i: GroceryOrderItem) => i.hostId === hostId
            );
            if (!hostItems.length) return null;
            return parseOrder(d.id, { ...data, items: hostItems });
          })
          .filter(Boolean) as GroceryOrder[];

        dispatch(groceriesOrderSlice.actions.setOrders(orders));
      },
      (err) => dispatch(groceriesOrderSlice.actions.setError(err.message))
    );

    dispatch(groceriesOrderSlice.actions.setUnsubscribe(unsub));
    return unsub;
  };

/* Stop active listener */
export const stopGroceryOrdersListener =
  () => (dispatch: AppDispatch, getState: () => RootState) => {
    const { unsubscribe } = getState().groceryOrders;
    if (unsubscribe) {
      unsubscribe();
      dispatch(groceriesOrderSlice.actions.setUnsubscribe(undefined));
    }
  };

/* ─────────────────── HOST STATS ─────────────────── */

export const fetchHostGroceryOrderStats = createAsyncThunk<
  { total: number; delivered: number; pending: number; profit: number },
  string,
  { rejectValue: string }
>(
  "groceryOrders/fetchHostStats",
  async (hostId, { rejectWithValue }) => {
    try {
      if (!hostId) throw new Error("Missing hostId");
      const snap = await getDocs(
        query(collection(db, "GroceryOrders"), where("hostId", "==", hostId))
      );
      let total = 0, delivered = 0, pending = 0, profit = 0;
      snap.forEach((d) => {
        const data = d.data() as GroceryOrder;
        total++;
        if (data.isDelivered) {
          delivered++;
          profit += data.hostProfits?.[hostId]?.hostProfit ?? data.hostProfit ?? 0;
        } else {
          pending++;
        }
      });
      return { total, delivered, pending, profit };
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch host grocery stats");
    }
  }
);

/* ─────────────────── SLICE ─────────────────── */

const groceriesOrderSlice = createSlice({
  name: "groceryOrders",
  initialState,
  reducers: {
    setOrders:      (state, a: PayloadAction<GroceryOrder[]>)       => { state.orders = a.payload; },
    setCurrentOrder:(state, a: PayloadAction<GroceryOrder | null>)  => { state.currentOrder = a.payload; },
    setError:       (state, a: PayloadAction<string | null>)        => { state.error = a.payload; },
    setUnsubscribe: (state, a: PayloadAction<(()=>void)|undefined>) => { state.unsubscribe = a.payload; },
    clearOrders:    (state) => { state.orders = []; state.currentOrder = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(upsertGroceryOrder.fulfilled, (state, a) => {
        const idx = state.orders.findIndex((o) => o.id === a.payload.id);
        if (idx >= 0) {
          state.orders[idx] = a.payload;
          if (state.currentOrder?.id === a.payload.id) state.currentOrder = a.payload;
        } else {
          state.orders.unshift(a.payload);
        }
      })
      .addCase(upsertGroceryOrder.rejected, (state, a) => {
        state.error = a.payload ?? "Failed to upsert grocery order";
      })
      .addCase(fetchHostGroceryOrderStats.fulfilled, (state, a) => {
        state.hostDeliveredCount  = a.payload.delivered;
        state.hostDeliveredProfit = a.payload.profit;
      });
  },
});

export const {
  setOrders,
  setCurrentOrder,
  setError,
  setUnsubscribe,
  clearOrders,
} = groceriesOrderSlice.actions;

export default groceriesOrderSlice.reducer;

/*
  ADD TO STORE:
  import groceryOrdersReducer from "./groceriesOrderSlice";
  groceryOrders: groceryOrdersReducer,
*/
